// Turns a raw yasna entry (getById's JSON) into what the student panel shows:
// the word and its meaning, its tags, and every other card as key » value
// rows, "synonyms: heureux » happy" (Requirement 53).
import { meaningOf } from './meaning';

// Shown whatever the teacher picked, as yasna does: tags, the meaning, a
// spelling variant.
const ALWAYS = new Set(['meaning', 'spelling variant']);
const alwaysShown = (card) => card.card_type === 'tag' || ALWAYS.has(card.label);

const text = (v) => (typeof v === 'string' && v.trim() ? v.trim() : null);

// Grammar is kept out of the way (Requirement 56): hoja helps learners pick
// up a language from use, not from terminology. Tags naming a part of speech,
// number or gender, and the cards below, go to a collapsed "Grammar"
// section; usage tags (informal, slang, verlan, idiom, …) stay up front.
const GRAMMAR_TAGS = new Set([
  'noun', 'verb', 'adjective', 'adverb', 'pronoun', 'preposition', 'conjunction', 'interjection',
  'article', 'contraction', 'determiner', 'auxiliary', 'participle', 'infinitive',
  'singular', 'plural', 'masculine', 'feminine', 'neuter',
]);
const GRAMMAR_CARDS = new Set(['grammar', 'forms', 'auxiliary verb', 'elision']);
const isGrammarCard = (card) => card.card_type === 'grammar' || GRAMMAR_CARDS.has(text(card.label));
// Links to the base word stay up front, without their grammatical note
// ("imiter » to imitate", not "3rd person singular, present tense").
const BASE_WORD_CARDS = new Set(['inflected form of', 'conjugated form of']);

/**
 * One entry of a card's list as key » value: "heureux » happy". An example
 * has no word: its sentence is the key and its translation the value.
 */
export function pairOf(item) {
  if (!item || typeof item !== 'object') return null;
  const word = text(item.word);
  const pair = word
    ? { key: word, value: text(item.meaning) || text(item.translation) }
    : { key: text(item.meaning), value: text(item.translation) };
  if (!pair.key) return null;
  const notes = [text(item.note)];
  if (word && text(item.meaning) && text(item.translation)) notes.push(text(item.translation));
  return {
    ...pair,
    note: notes.filter(Boolean).join(' · ') || null,
    example: text(item.example) ? { key: text(item.example), value: text(item.example_translation) } : null,
    reference: text(item.reference),
  };
}

const pairs = (list) => (Array.isArray(list) ? list.map(pairOf).filter(Boolean) : []);

// A card's rows: a single "label » value", or a label over its list.
function rowsOf(card) {
  if (BASE_WORD_CARDS.has(text(card.label))) {
    const list = pairs(card.entries || card.items).map((p) => ({ ...p, note: null }));
    return list.length ? [{ label: text(card.label), pairs: list }] : [];
  }
  if (card.card_type === 'grammar') {
    return [
      { label: 'grammar', pairs: pairs((card.attributes || []).map((a) => ({ word: a.label, meaning: a.value }))) },
      { label: 'other tenses', pairs: pairs(card.other_tenses) },
    ].filter((r) => r.pairs.length);
  }
  const label = text(card.label);
  if (!label) return [];
  if (text(card.value)) return [{ label, value: text(card.value) }];
  const list = pairs(card.entries || card.items);
  return list.length ? [{ label, pairs: list }] : [];
}

/**
 * { word, meaning, tags, rows, grammar }. Each row is { label, value } or
 * { label, pairs }; `card` numbers the card it comes from (a grammar card
 * gives two rows), and `shown` says whether a student sees it before
 * "More": when the teacher marked cards `expanded`, only those (and the
 * always-shown ones); otherwise every card.
 */
export function entryView(entry) {
  if (!entry) return null;
  const cards = Array.isArray(entry.cards) ? entry.cards.filter((c) => c && typeof c === 'object') : [];
  const meaning = meaningOf(entry);
  // The first card is the meaning in the header; don't show it twice.
  const body = text(cards[0] && cards[0].value) ? cards.slice(1) : cards;
  const picked = cards.some((c) => typeof c.expanded === 'boolean' && !alwaysShown(c));
  const allTags = body.filter((c) => c.card_type === 'tag').map((c) => text(c.label)).filter(Boolean);
  const isGrammarTag = (t) => GRAMMAR_TAGS.has(t.toLowerCase());
  const tags = allTags.filter((t) => !isGrammarTag(t));
  const numbered = body.filter((c) => c.card_type !== 'tag').map((c, card) => ({ c, card }));
  const rows = numbered
    .filter(({ c }) => !isGrammarCard(c))
    .flatMap(({ c, card }) => rowsOf(c).map((r) => ({ ...r, card, shown: !picked || alwaysShown(c) || c.expanded === true })));
  const grammar = {
    // "3rd person singular, present tense of imiter"
    form: text(entry.sense_label),
    tags: allTags.filter(isGrammarTag),
    rows: numbered.filter(({ c }) => isGrammarCard(c)).flatMap(({ c, card }) => rowsOf(c).map((r) => ({ ...r, card }))),
  };
  return { word: text(entry.word) || '', meaning, tags, rows, grammar };
}
